import { Briefcase, UserRound } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export type DemoView = "recruiter" | "candidate";

const VIEWS: { id: DemoView; label: string; icon: typeof Briefcase }[] = [
  { id: "recruiter", label: "Recruiter's view", icon: Briefcase },
  { id: "candidate", label: "Candidate's view", icon: UserRound },
];

const TOASTS: Record<DemoView, { title: string; body: string }> = {
  candidate: {
    title: "You're the candidate now",
    body: "Answer out loud, like a real interview.",
  },
  recruiter: {
    title: "You're the recruiter now",
    body: "See what a hiring team sees after the interview.",
  },
};

const TOAST_MS = 4500;

type Props = {
  view: DemoView;
  /** Show the notice on first render too (the results page opens in the recruiter's view). */
  announceOnMount?: boolean;
};

/**
 * Which side of the hiring process the visitor is looking at. The demo switches roles twice
 * (recruiter sends, candidate answers, recruiter reviews), so the current side is always marked and
 * a short notice pops up at each switch.
 */
export const DemoViewMap = ({ view, announceOnMount = false }: Props) => {
  const [toast, setToast] = useState<DemoView | null>(announceOnMount ? view : null);
  const previous = useRef(view);

  useEffect(() => {
    if (previous.current === view) return;
    previous.current = view;
    setToast(view);
  }, [view]);

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast(null), TOAST_MS);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  return (
    <>
      <ol className="a25-view-map" aria-label="Whose view you're seeing">
        {VIEWS.map(({ id, label, icon: Icon }) => (
          <li key={id} aria-current={id === view ? "true" : undefined} data-view={id}>
            <Icon size={13} aria-hidden="true" />
            {label}
          </li>
        ))}
      </ol>
      <div className="a25-view-toast-slot" aria-live="polite">
        {toast && (
          <div key={toast} className="a25-view-toast" data-view={toast}>
            <strong>{TOASTS[toast].title}</strong>
            <span>{TOASTS[toast].body}</span>
          </div>
        )}
      </div>
    </>
  );
};
